/* ============================================================
   INNOR — homepage
   Hero : court pin auto-porté (scale + flash blanc de sortie).
   Récit : on entre, on ne descend pas. Chaque planche (image entière
   dans son cadre + texte en vis-à-vis) arrive de loin, se pose nette,
   puis nous dépasse pendant que la suivante approche. Le scroll pilote
   une distance ; rien ne translate verticalement.
   ============================================================ */
(function () {
  'use strict';

  const REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Mobile : une seule animation, celle de l'entrée ----------
     Décision client : sur petit écran on ne garde que la première animation,
     hero → frame 1. Le récit qui suit redevient une page qu'on fait défiler
     normalement — aucun chapitre ne s'approche, la galerie ne s'envole plus,
     et surtout plus aucun verrou de scroll passé l'atterrissage.

     Le seuil est celui de la mise en page mobile de innor-scene.css (899px),
     pas celui du hero 3D (749px) : ce sont deux bascules différentes.

     Évalué en direct plutôt qu'une fois pour toutes — une fenêtre de bureau
     rétrécie sous le seuil doit elle aussi cesser d'intercepter les gestes,
     sinon on se retrouve avec la mise en page mobile et la mécanique bureau. */
  const MQ_MOBILE = window.matchMedia('(max-width: 899px)');
  const mobile = () => MQ_MOBILE.matches;


  /* ---------- Verrou d'entrée ----------
     Sur le hero, une impulsion ne peut faire qu'une chose : lancer la
     séquence. Rien ne défile, jamais.

     Le verrou lui-même est posé plus tôt, par le script en ligne en tête de
     main-scene.liquid : ce fichier est `defer`, il boote trop tard sur une
     connexion lente pour arrêter un geste. On ne fait ici que le reprendre —
     et signaler qu'on l'a repris, pour que le filet de sécurité de ce script
     en ligne ne le lève pas dans notre dos. */
  if (!REDUIT && document.querySelector('[data-hero-pin]')) {
    window.INNOR_HERO_PRET = true;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    document.documentElement.style.overflow = 'hidden';
    window.scrollTo(0, 0);
  }

  /* Lenis n'existe pas encore au moment du verrou initial : on retombe sur
     l'overflow seul, INNOR.lockScroll prend le relais dès qu'il est là. */
  function verrou(actif) {
    if (window.INNOR && window.INNOR.lockScroll) window.INNOR.lockScroll(actif);
    else document.documentElement.style.overflow = actif ? 'hidden' : '';
  }

  /* Touches qui font défiler une page : elles n'ont rien à faire sur le hero.
     Toutes les autres (Tab, Échap, raccourcis) passent — l'en-tête et le
     panier restent utilisables pendant que la croix tourne. */
  const TOUCHES_SCROLL = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar'];
  const TOUCHES_LANCE = ['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar'];
  /* Après l'atterrissage, la fin du geste continue d'arriver : inertie de
     trackpad, molette lancée. On l'absorbe encore un instant, sinon ce qui
     reste de l'impulsion qui a LANCÉ la séquence se rejoue sur le premier
     chapitre et le traverse d'un trait. */
  const HERO_GRACE = 700;

  /* Couper une impulsion net. En CAPTURE sur window on passe avant Lenis, qui
     écoute en bulle : stopPropagation suffit alors à ce qu'il ne la voie
     jamais. C'est le point clé — un simple preventDefault en bulle arrivait
     trop tard, Lenis avait déjà encaissé le delta et repartait avec. */
  function avaler(e) {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
  }

  function ecouter(handler) {
    ['wheel', 'touchmove', 'keydown'].forEach((type) => {
      window.addEventListener(type, handler, { capture: true, passive: false });
    });
  }

  /* ---------- Hero : croix 3D + pin court + flash de sortie ---------- */
  class HeroPin {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector('[data-hero]');
      this.flash = root.querySelector('[data-hero-flash]');
      this.haut = root.querySelector('.h3d-line--top');
      this.bas = root.querySelector('.h3d-line--bottom');
      this.stage = root.querySelector('[data-hero3d]');
      this.cue = root.querySelector('.hero-pin__scrollcue');
      // Échelle de repos lue dans le CSS : elle diffère selon le breakpoint.
      this.repos = this.stage
        ? parseFloat(getComputedStyle(this.stage).getPropertyValue('--h3d-repos')) || 0.15
        : 0.15;

      this.initHero3d();
      this.initSequence();
    }

    /* ---------- Croix : WebGL partout, sprite en filet ----------
       Le mobile jouait une spritesheet de 40 vues, soit une image toutes les
       350 ms — 2,9 vues par seconde, 9° de saut à chaque fois. C'était LA
       raison pour laquelle le hero paraissait saccadé sur téléphone, et aucune
       optimisation de chargement n'y pouvait rien : c'est le rendu lui-même
       qui était en marches d'escalier. Le sprite était de surcroît le chemin
       le plus lourd (383 ko contre ~208 ko pour la 3D, compressés).

       Trois filets, du moins cher au plus sûr :
         1. appareil manifestement trop juste  → le moteur n'est même pas
            demandé (tri fait dans main-scene.liquid, seul endroit qui puisse
            encore éviter la requête) : pas de promesse, donc sprite
         2. WebGL absent ou refusé             → sprite
         3. 3D lancée mais qui ne tient pas    → sprite (voir surveiller3d)

       Le moteur n'est plus une balise <script> du HTML : main-scene.liquid
       l'injecte et publie window.INNOR_H3D. Comme il ne retarde plus
       DOMContentLoaded, on ne peut plus se contenter de tester
       window.INNOR_HERO3D ici — il faut l'attendre, sinon on retomberait sur
       le sprite chaque fois que three.js arrive après le reste de la page. */
    initHero3d() {
      const stage = this.root.querySelector('[data-hero3d]');
      if (!stage) return;

      const filet = () => this.initSprite(stage);

      const essayer3d = () => {
        if (!window.INNOR_HERO3D || !window.INNOR_LOGO_SHAPE) return false;
        try {
          this.hero3d = window.INNOR_HERO3D(stage, window.INNOR_LOGO_SHAPE, {
            period: 14,
            patine: stage.dataset.patine
          });
          return true;
        } catch (e) {
          return false;                       // WebGL indisponible → sprite
        }
      };

      const lancer = () => {
        if (!essayer3d()) { filet(); return; }
        stage.dataset.rendu = '3d';
        this.surveiller3d(filet);
      };

      if (window.INNOR_H3D) window.INNOR_H3D.then(lancer);
      else lancer();                          // moteur déjà présent
    }

    /* Troisième filet : on regarde tourner la VRAIE croix avant de la garder.
       Les capacités déclarées mentent — un iPhone n'annonce ni mémoire ni
       cœurs, un Android d'entrée de gamme en annonce quatre et rame quand
       même. Seule la mesure tranche.

       La fenêtre s'ouvre après l'événement `load` : pendant le chargement, les
       photos du récit et le pixel Facebook font des à-coups qui n'ont rien à
       voir avec la croix, et condamneraient la 3D à tort. On échantillonne
       donc le régime de croisière, une seconde et demie.

       Une bascule visible à la troisième seconde vaut mieux qu'une rotation
       qui saccade toute la visite. */
    surveiller3d(filet) {
      const mesurer = () => {
        const debut = performance.now();
        let precedent = debut, lentes = 0, total = 0;
        const voir = (now) => {
          const dt = now - precedent;
          precedent = now;
          total++;
          if (dt > 50) lentes++;              // sous 20 im/s
          if (now - debut < 1500) { requestAnimationFrame(voir); return; }
          /* Un tiers d'images trop longues : ce n'est plus l'à-coup isolé,
             c'est le régime. On rend la main au sprite. */
          if (total > 10 && lentes / total > 0.34) {
            if (this.hero3d && this.hero3d.destroy) this.hero3d.destroy();
            this.hero3d = null;
            filet();
          }
        };
        requestAnimationFrame(voir);
      };
      if (document.readyState === 'complete') setTimeout(mesurer, 400);
      else window.addEventListener('load', () => setTimeout(mesurer, 400));
    }

    /* Sprite : 40 vues pré-rendues, un tour en 14 s — soit une image toutes
       les 350 ms. La boucle redessinait à 25 im/s : sept à neuf fois la MÊME
       case, pour rien. On ne dessine plus qu'au changement de case.

       Et elle s'arrête quand le hero n'est plus à l'écran ou l'onglet en
       arrière-plan. Elle tournait jusqu'au pied de page : sur un téléphone
       c'est de la batterie et de la chaleur, et le bridage thermique qui suit
       saccade tout le reste du récit. */
    initSprite(stage) {
      const spriteUrl = stage.dataset.sprite;
      if (!spriteUrl) return;
      /* Témoin du chemin réellement pris. Avec trois niveaux de repli, savoir
         lequel s'est déclenché sur un appareil donné ne se devine pas : il
         suffit maintenant d'inspecter [data-hero3d] pour le lire. */
      stage.dataset.rendu = 'sprite';
      const canvas = document.createElement('canvas');
      const size = stage.clientWidth || 300;
      canvas.width = canvas.height = Math.min(size * (window.devicePixelRatio || 1), 520);
      canvas.style.width = canvas.style.height = '100%';
      stage.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.src = spriteUrl;
      const COLS = 8, N = 40, CELL = 260, PERIODE = 14000;
      let derniere = -1;
      img.decode().then(() => {
        const loop = (now) => {
          const i = Math.floor((now / (PERIODE / N)) % N);
          if (i !== derniere) {
            derniere = i;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(
              img,
              (i % COLS) * CELL, Math.floor(i / COLS) * CELL, CELL, CELL,
              0, 0, canvas.width, canvas.height
            );
          }
          this.spriteRaf = requestAnimationFrame(loop);
        };

        let vu = true, actif = false;
        const marche = () => {
          const doit = vu && !document.hidden;
          if (doit && !actif) { actif = true; this.spriteRaf = requestAnimationFrame(loop); }
          else if (!doit && actif) { actif = false; cancelAnimationFrame(this.spriteRaf); }
        };
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entrees) => {
            vu = entrees[0].isIntersecting;
            marche();
          }).observe(stage);
        }
        document.addEventListener('visibilitychange', marche);
        marche();
      }).catch(() => {});
    }

    /* ---------- Séquence d'entrée : une impulsion, scroll verrouillé ----------
       Le scroll ne pilote pas la séquence image par image — il la DÉCLENCHE.
       Une première impulsion (molette, doigt, flèche) et la séquence se joue
       seule, d'un bloc, scroll bloqué. On ne peut ni la parcourir trop vite
       ni s'arrêter au milieu : elle se lit toujours au même rythme.

       Remarque : une itération précédente avait été rejetée parce qu'elle
       verrouillait le scroll. Le verrou est ici demandé explicitement, et
       il est borné — 2,8 s, uniquement sur le hero. */
    initSequence() {
      if (typeof gsap === 'undefined') { verrou(false); return; }

      this.etat = { fuite: 0, mots: 1, zoom: 0, voile: 0 };
      /* arme  : on attend l'impulsion, aucune autre issue
         joue  : la séquence se déroule, toute impulsion est avalée
         grace : atterri sur le premier chapitre, on absorbe la fin du geste
         libre : le récit a la main */
      this.phase = 'arme';

      this.tl = gsap.timeline({
        paused: true,
        onUpdate: () => this.appliquer(),
        onComplete: () => this.terminer()
      });
      /* Les mots partent les premiers et se croisent en s'écartant ;
         l'entrée dans la croix démarre pendant qu'ils sortent encore, et
         s'étire (power2.in) pour rester lente longtemps avant de plonger. */
      this.tl
        .to(this.etat, { duration: 1.25, fuite: 1, ease: 'power2.inOut' }, 0)
        .to(this.etat, { duration: 0.95, mots: 0, ease: 'power1.in' }, 0.35)
        .to(this.etat, { duration: 2.1, zoom: 1, ease: 'power2.in' }, 0.6)
        .to(this.etat, { duration: 0.85, voile: 1, ease: 'power1.inOut' }, 1.95);

      ecouter((e) => this.intercepter(e));

      window.addEventListener('scroll', () => {
        /* Tant que le hero a la main, il ne doit y avoir AUCUN scroll. Filet
           pour ce qui échappe aux événements d'entrée : glissement de la
           barre de défilement, ancre, restauration du navigateur, panneau
           latéral qui rend l'overflow en se fermant. */
        if (this.phase === 'arme') {
          if (window.scrollY > 0) window.scrollTo(0, 0);
          return;
        }
        /* Retour tout en haut : on réarme, la séquence pourra rejouer. */
        if (this.phase === 'libre' && window.scrollY < 4) this.reinitialiser();
      }, { passive: true });

      verrou(true);
      this.appliquer();
    }

    /* Seul point d'entrée des impulsions tant que le hero a la main. */
    intercepter(e) {
      if (this.phase === 'libre') return;

      if (e.type === 'keydown') {
        if (TOUCHES_SCROLL.indexOf(e.key) === -1) return;   // Tab, Échap, raccourcis
        avaler(e);
        if (this.phase === 'arme' && TOUCHES_LANCE.indexOf(e.key) !== -1) this.lancer();
        return;
      }

      avaler(e);
      if (this.phase !== 'arme') return;                    // joue / grace : rien d'autre
      if (e.type === 'wheel' && e.deltaY <= 0) return;      // remonter ne lance pas
      this.lancer();
    }

    lancer() {
      if (this.phase !== 'arme') return;
      this.phase = 'joue';
      verrou(true);
      this.tl.play(0);
    }

    terminer() {
      /* On rend le scroll — il doit l'être pour que le saut prenne — mais on
         reste en 'grace' : les impulsions sont encore avalées le temps que
         l'inertie du geste s'éteigne. Sans ça, un gros scroll traversait le
         premier chapitre dans la foulée de l'atterrissage. */
      this.phase = 'grace';
      clearTimeout(this.tGrace);
      this.tGrace = setTimeout(() => {
        if (this.phase === 'grace') this.phase = 'libre';
      }, HERO_GRACE);
      verrou(false);

      /* On atterrit là où le premier chapitre est déjà PRÉSENT, pas à son
         point de départ : sinon le voile se lève sur un écran vide et il
         faut scroller encore pour voir quoi que ce soit. */
      const tunnel = document.querySelector('[data-tunnel]');
      const premier = tunnel && tunnel.querySelector('.zoom-act');
      if (!premier) return;
      const haut = premier.getBoundingClientRect().top + window.scrollY;
      const course = premier.offsetHeight - window.innerHeight;
      /* On vise le milieu de la PRÉSENCE du premier chapitre, pas le milieu
         de sa course. Pour la galerie les deux ne coïncident pas : sa course
         se répartit sur quatre temps, et 0,5 tomberait pendant l'évasion
         latérale — on atterrirait sur des cadres déjà en train de partir.

         Sur mobile le chapitre n'a plus de course du tout : il fait un écran
         pile, image plein cadre. On se pose sur son bord haut, sinon un
         arrondi de course suffirait à décoller l'image du haut de l'écran. */
      const fraction = premier.classList.contains('zoom-act--galerie') ? 0.42 : 0.5;
      const cible = mobile() ? haut : haut + course * fraction;
      const lenis = window.INNOR && window.INNOR.lenis;
      if (lenis && lenis.scrollTo) lenis.scrollTo(cible, { immediate: true, force: true });
      else window.scrollTo(0, cible);

      /* Filet, comme pour l'envol de la galerie : selon la version de Lenis
         un scrollTo immédiat peut n'être appliqué qu'au tick suivant, voire
         avalé. On vérifie à la frame d'après — un saut manqué nous laisserait
         en haut de page, séquence rejouée en boucle. */
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - cible) > 40) window.scrollTo(0, cible);
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.update();
      });
    }

    reinitialiser() {
      clearTimeout(this.tGrace);
      this.tl.pause(0);
      this.etat.fuite = 0; this.etat.mots = 1; this.etat.zoom = 0; this.etat.voile = 0;
      this.appliquer();
      this.phase = 'arme';
      verrou(true);
      window.scrollTo(0, 0);
    }

    appliquer() {
      const e = this.etat;
      if (this.haut) {
        this.haut.style.setProperty('--fuite', (-e.fuite * 78).toFixed(2) + 'vh');
        this.haut.style.opacity = e.mots.toFixed(3);
      }
      if (this.bas) {
        this.bas.style.setProperty('--fuite', (e.fuite * 78).toFixed(2) + 'vh');
        this.bas.style.opacity = e.mots.toFixed(3);
      }
      if (this.stage) {
        const echelle = this.repos + e.zoom * (2.2 - this.repos);
        this.stage.style.setProperty('--h3d-echelle', echelle.toFixed(3));
      }
      if (this.flash) this.flash.style.opacity = e.voile.toFixed(3);
      if (this.cue) this.cue.style.opacity = (1 - Math.min(1, e.fuite * 3)).toFixed(3);
    }
  }

  /* ---------- Chapitre : approche en profondeur ----------
     Le scroll ne déplace rien verticalement : il pilote une DISTANCE.
     Trois temps sur la longueur propre du chapitre (0..1) :

       0 → ARRIVEE  : la planche vient de loin. Échelle 0,60 → 1, flou
                      8px → 0, opacité 0 → 1. On s'approche d'elle.
       → PRESENCE   : elle tient, nette, à l'échelle 1. On est devant.
       → 1          : elle nous dépasse (1 → 1,14) en se dissolvant. La
                      suivante, dont la section chevauche celle-ci de
                      45vh, a déjà commencé son approche derrière.

     L'échelle porte sur la PLANCHE ENTIÈRE, jamais sur l'image seule :
     la photo reste en contain dans son cadre, donc son cadrage ne bouge
     pas d'un pixel — elle se rapproche, elle ne se recadre pas. C'est ce
     qui distingue cette mécanique de l'ancien tunnel qui rognait.

     L'amplitude est volontairement courte (0,60 → 1,14) : au-delà on
     retombe dans le "trop zoomé" déjà rejeté. Ce sont le flou et le halo
     qui portent la profondeur, pas l'agrandissement. */
  const ARRIVEE = 0.38;
  const PRESENCE = 0.70;

  class ActeProfondeur {
    constructor(root) {
      this.root = root;
      this.plaque = root.querySelector('.zoom-act__viewport');
      this.copy = root.querySelector('[data-zoom-copy]');
      /* Le dernier chapitre ne nous depasse pas : il arrive et il reste.
         Sans ca il se dissolvait dans un ecran vide avant le footer — et
         comme c'est lui qui porte le CTA boutique, le bouton devenait
         incliquable en fin de scroll. */
      this.dernier = !root.nextElementSibling ||
                     !root.nextElementSibling.classList.contains('zoom-act');
      this.init();
    }

    init() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
      /* 'top top' -> 'bottom bottom' : la fenetre d'animation coincide avec
         la fenetre ou la planche est COLLEE, donc centree a l'ecran. Avec
         'top bottom' elle s'animait pendant qu'elle remontait encore depuis
         le bas de l'ecran — on ressentait alors une translation verticale,
         exactement ce qu'on cherche a supprimer. */
      ScrollTrigger.create({
        trigger: this.root,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.35,
        onUpdate: (self) => this.update(self.progress),
        onLeaveBack: () => this.update(0),
        onLeave: () => this.update(1)
      });
      this.update(0);
    }

    update(p) {
      let echelle, opacite, flou;
      if (p < ARRIVEE) {
        const t = p / ARRIVEE;
        echelle = 0.6 + t * 0.4;
        opacite = Math.min(1, t / 0.35);
        flou = 8 * (1 - t);
      } else if (p < PRESENCE) {
        echelle = 1; opacite = 1; flou = 0;
      } else if (this.dernier) {
        echelle = 1; opacite = 1; flou = 0;
      } else {
        const t = (p - PRESENCE) / (1 - PRESENCE);
        echelle = 1 + t * 0.14;
        opacite = Math.max(0, 1 - t / 0.8);
        flou = 5 * t;
      }

      const st = this.plaque.style;
      st.transform = 'scale(' + echelle.toFixed(3) + ')';
      st.opacity = opacite.toFixed(3);
      st.filter = flou > 0.06 ? 'blur(' + flou.toFixed(2) + 'px)' : 'none';
      st.setProperty('--proche', opacite.toFixed(3));
      st.pointerEvents = opacite > 0.5 ? 'auto' : 'none';

      // Le texte se pose juste après l'image, pour qu'on regarde la pièce
      // avant de lire — sans quoi les deux arrivent en même temps et se
      // disputent l'attention.
      const c = Math.max(0, Math.min(1, (p - 0.22) / 0.16));
      this.copy.style.opacity = c.toFixed(3);
      this.copy.style.transform = 'translateY(' + ((1 - c) * 1.1).toFixed(2) + 'rem)';
    }
  }

  /* ---------- Chapitre galerie : arrivée au scroll, envol verrouillé ----------
     Deux régimes, et c'est là toute la différence avec les autres chapitres.

     AU SCROLL (0..1 sur la longueur du chapitre) :
       0 → ARRIVEE : la planche vient de loin, comme partout ailleurs.
       → ARME      : elle tient, nette. Les légendes sont survolables, et à
                     partir de G_ARME une impulsion vers le bas arme la
                     sortie au lieu de faire défiler.

     HORS SCROLL — l'ENVOL :
       Les trois cadres montent d'un bloc et sortent par le haut, scroll
       verrouillé, sur la durée et la courbe exactes du zoom dans la croix
       du hero. Le geste ne consomme aucun scroll : on ne peut ni le
       traverser trop vite ni s'arrêter au milieu, et il se lit toujours au
       même rythme. Une fois les cadres hors champ, on rejoint le chapitre
       suivant d'un saut — l'écran est vide, le saut ne se voit pas. */
  const G_ARRIVEE = 0.30;
  const G_ARME = 0.55;      /* à partir d'ici, une impulsion vers le bas déclenche l'envol */

  /* Envol — chaque cadre a son propre départ, sa propre durée et sa propre
     course. C'est ce décalage qui fait qu'on SENT trois objets s'envoler et
     non un bloc qui glisse : les côtés lâchent en premier, le centre part
     en dernier et monte le plus loin. La courbe reste power2.in partout,
     celle du zoom dans la croix du hero — c'est la famille de mouvement qui
     est commune, plus la vitesse. */
  const G_VOL = {
    gauche: { retard: 0.00, duree: 1.50, course: -150 },
    droite: { retard: 0.10, duree: 1.60, course: -145 },
    centre: { retard: 0.20, duree: 1.75, course: -168 },
    cta:    { retard: 0.00, duree: 1.25, course:  120 }   /* vers le BAS */
  };
  const G_ENVOL_EASE = 'power2.in';
  /* Voile : le rôle exact du flash blanc du hero, en noir de tunnel. Il
     couvre le changement de chapitre, qui sautait à l'œil sans lui.
     Il monte AVANT que le dernier cadre soit sorti (1,45 contre 1,95) : le
     temps mort entre les deux chapitres vient de là, pas du vol lui-même. */
  const G_VOILE_MONTE = 1.45, G_VOILE_MONTE_D = 0.45;
  const G_VOILE_DESCEND_D = 0.55;

  class ActeGalerie {
    constructor(root) {
      this.root = root;
      this.plaque = root.querySelector('.zoom-act__viewport');
      this.copy = root.querySelector('[data-zoom-copy]');
      this.gauche = root.querySelector('[data-galerie-gauche]');
      this.centre = root.querySelector('[data-galerie-centre]');
      this.droite = root.querySelector('[data-galerie-droite]');
      this.init();
    }

    init() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
      this.p = 0;
      this.proche = 0;
      /* Présence de la planche, indépendante du scroll : tombe à 0 une fois
         l'envol joué, remonte quand on revient en arrière. */
      this.retour = 1;
      this.envol = { gauche: 0, droite: 0, centre: 0, cta: 0, voile: 0 };
      this.jouee = false;
      this.enVol = false;

      ScrollTrigger.create({
        trigger: this.root,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.35,
        onUpdate: (self) => { this.p = self.progress; this.update(self.progress); },
        onLeaveBack: () => { this.p = 0; this.revenir(true); this.update(0); },
        onLeave: () => { this.p = 1; this.update(1); },
        /* Retour depuis le chapitre suivant : c'est l'instant EXACT où la
           galerie redevient l'acte de premier plan. On y remet les cadres en
           place et on la fait réapparaître en fondu — plus tôt on remontait à
           travers une planche vide, plus tard les cadres surgissaient d'un
           coup. */
        onEnterBack: () => this.revenir()
      });

      this.initEnvol();
      this.update(0);
    }

    /* ---------- Envol : séquence auto-portée, scroll verrouillé ----------
       Même mécanique que la séquence d'entrée du hero : le scroll ne pilote
       pas le geste, il le DÉCLENCHE. Une impulsion vers le bas une fois la
       galerie posée, et les trois cadres montent d'un bloc pendant que le
       scroll est bloqué. On ne peut donc ni le parcourir trop vite, ni
       s'arrêter au milieu.

       Le déplacement porte sur la planche entière plutôt que sur chaque
       cadre : c'est la seule façon de garantir qu'ils montent EXACTEMENT à
       la même vitesse, sans dérive d'arrondi entre trois transforms. */
    initEnvol() {
      this.voile = document.querySelector('[data-scene-voile]');
      this.tlEnvol = gsap.timeline({
        paused: true,
        onUpdate: () => this.appliquerEnvol(),
        onComplete: () => this.basculer()
      });

      ['gauche', 'droite', 'centre', 'cta'].forEach((cle) => {
        const v = G_VOL[cle];
        this.tlEnvol.to(this.envol, {
          duration: v.duree, ease: G_ENVOL_EASE, [cle]: 1
        }, v.retard);
      });

      /* La timeline s'ARRÊTE voile plein. Le saut et le retrait du voile sont
         traités dans onComplete, hors timeline : le verrou doit être levé
         pour de bon avant de déplacer le scroll, et lenis.stop() annule toute
         animation en cours — le reposer aussitôt après tuait le saut. */
      this.tlEnvol
        .to(this.envol, { duration: G_VOILE_MONTE_D, voile: 1, ease: 'power1.inOut' }, G_VOILE_MONTE);

      ecouter((e) => this.intercepter(e));

      /* Le réarmement est piloté par onEnterBack / onLeaveBack du déclencheur,
         pas par un seuil de progression : ces deux événements tombent aux
         frontières exactes du chapitre, un seuil arbitraire réinitialisait au
         milieu de l'écran. */
    }

    /* Position de page où l'envol s'arme. Le scroll ne va pas au-delà tant
       qu'il n'a pas eu lieu : c'est la seule sortie de la galerie. */
    ligneArmement() {
      const haut = this.root.getBoundingClientRect().top + window.scrollY;
      const course = this.root.offsetHeight - window.innerHeight;
      return haut + course * G_ARME;
    }

    /* L'ARRIVÉE de la planche reste pilotée par le scroll — c'est voulu, on
       s'approche d'elle. Mais aucun geste ne franchit la ligne d'armement :
       arrivé là, il est avalé et il ne fait qu'une chose, lancer l'envol.

       Sans ce plafond, un seul gros cran emmenait la progression de 0,42 à
       plus de 1, donc au-delà de la fenêtre d'armement — l'envol n'était
       jamais déclenché et on traversait la galerie et tout le récit d'un
       trait, jusqu'au pied de page. Mesuré : 1429 → 5001 en un geste. */
    intercepter(e) {
      if (mobile()) return;                       // mobile : plus aucun verrou après le hero
      if (this.jouee) return;                     // envol joué, le récit reprend la main
      if (this.enVol) { avaler(e); return; }      // pendant l'envol, plus rien ne passe
      if (this.p <= 0 || this.p >= 1) return;     // la galerie n'est pas au premier plan

      if (e.type === 'keydown') {
        if (TOUCHES_LANCE.indexOf(e.key) === -1) return;  // remonter reste libre
        avaler(e);
        this.lancer();
        return;
      }
      if (e.type === 'wheel' && e.deltaY <= 0) return;    // remonter reste libre

      const delta = e.type === 'wheel' ? e.deltaY : 0;
      if (this.p < G_ARME && window.scrollY + delta < this.ligneArmement()) return;
      avaler(e);
      this.lancer();
    }

    lancer() {
      if (this.jouee || this.enVol) return;
      this.jouee = true;
      this.enVol = true;
      if (window.INNOR && window.INNOR.lockScroll) window.INNOR.lockScroll(true);
      this.tlEnvol.play(0);
    }

    /* Voile plein : on déverrouille pour de bon, on saute, on retire le voile.
       L'ordre est porteur de sens — le verrou pose overflow:hidden sur <html>
       et arrête Lenis, or lenis.stop() annule les animations en cours. Tant
       qu'il tient, aucun déplacement ne prend, et le reposer trop tôt annule
       celui qu'on vient de lancer. */
    basculer() {
      this.enVol = false;
      /* La planche est vidée de ses cadres : on l'éteint franchement. Elle
         reste dans le flux (sticky, z-index 5) et on va la traverser en
         remontant — autant qu'elle ne soit rien du tout plutôt qu'un cadre
         vide. Le voile est plein, la bascule ne se voit pas. */
      this.retour = 0;
      if (window.INNOR && window.INNOR.lockScroll) window.INNOR.lockScroll(false);

      const suivant = this.root.nextElementSibling;
      const cible = this.cibleSuivant(suivant);

      if (cible !== null) {
        const lenis = window.INNOR && window.INNOR.lenis;
        if (lenis && lenis.scrollTo) lenis.scrollTo(cible, { immediate: true, force: true });
        else window.scrollTo(0, cible);

        /* Filet : selon la version de Lenis, un scrollTo immédiat peut ne
           s'appliquer qu'au tick suivant — voire être avalé. On vérifie à la
           frame d'après et on corrige en natif si besoin, AVANT que le voile
           ne commence à se retirer. */
        requestAnimationFrame(() => {
          if (Math.abs(window.scrollY - cible) > 40) window.scrollTo(0, cible);
          if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.update();
          this.retirerVoile();
        });
        return;
      }
      this.retirerVoile();
    }

    cibleSuivant(suivant) {
      if (!suivant || !suivant.classList.contains('zoom-act')) return null;
      const haut = suivant.getBoundingClientRect().top + window.scrollY;
      const course = suivant.offsetHeight - window.innerHeight;
      return haut + course * 0.5;
    }

    retirerVoile() {
      gsap.to(this.envol, {
        duration: G_VOILE_DESCEND_D,
        voile: 0,
        ease: 'power1.inOut',
        delay: 0.05,
        onUpdate: () => this.appliquerEnvol()
      });
    }

    reinitialiser() {
      this.tlEnvol.pause(0);
      this.envol.gauche = this.envol.droite = this.envol.centre = 0;
      this.envol.cta = this.envol.voile = 0;
      this.jouee = false;
      this.appliquerEnvol();
    }

    /* Remise en état au retour arrière. Les cadres reprennent leur place
       pendant que la planche est encore éteinte, puis elle se rallume en
       fondu : on ne voit jamais le repositionnement, seulement l'apparition.
       immediat = true pour le retour tout en haut, où le fondu n'a pas lieu
       d'être (le hero reprend la main). */
    revenir(immediat) {
      if (!this.jouee || this.enVol) return;
      this.reinitialiser();
      gsap.killTweensOf(this);
      if (immediat) {
        this.retour = 1;
        this.update(this.p);
        return;
      }
      this.retour = 0;
      gsap.to(this, {
        retour: 1,
        duration: 0.45,
        ease: 'power1.out',
        onUpdate: () => this.update(this.p)
      });
    }

    /* Seuls les cadres et le CTA se déplacent. La planche, elle, ne bouge
       pas d'un pixel : c'est ce qui donne l'impression que des objets
       quittent un cadre resté en place, et non qu'on fait glisser l'écran. */
    appliquerEnvol() {
      const e = this.envol;
      const pose = (el, cle) => {
        if (!el) return;
        const v = e[cle];
        el.style.transform = 'translate3d(0, ' + (v * G_VOL[cle].course).toFixed(2) + 'vh, 0)';
      };
      pose(this.gauche, 'gauche');
      pose(this.droite, 'droite');
      pose(this.centre, 'centre');

      if (this.copy) {
        this.copy.style.transform = 'translate3d(0, ' + (e.cta * G_VOL.cta.course).toFixed(2) + 'vh, 0)';
      }

      /* Le halo suit le départ du dernier cadre, sinon il reste à briller
         sur une planche vide. */
      const parti = Math.max(e.gauche, e.droite, e.centre);
      this.plaque.style.setProperty('--proche', (this.proche * (1 - parti)).toFixed(3));

      if (this.voile) this.voile.style.opacity = e.voile.toFixed(3);
    }

    update(p) {
      let echelle = 1, opacite = 1, flou = 0;

      if (p < G_ARRIVEE) {
        const t = p / G_ARRIVEE;
        echelle = 0.6 + t * 0.4;
        opacite = Math.min(1, t / 0.35);
        flou = 8 * (1 - t);
      }

      const vue = opacite * this.retour;
      const st = this.plaque.style;
      st.transform = 'scale(' + echelle.toFixed(3) + ')';
      st.opacity = vue.toFixed(3);
      st.filter = flou > 0.06 ? 'blur(' + flou.toFixed(2) + 'px)' : 'none';
      this.proche = vue;
      st.setProperty('--proche', vue.toFixed(3));

      /* Les légendes ne sont survolables que posées, jamais en vol. */
      const vivant = p > 0.12 && !this.enVol && this.envol.centre === 0;

      /* Une fois l'envol joué, la planche reste collée et au-dessus du
         chapitre suivant (z-index 5 contre 4). Elle est vide et
         transparente, mais elle intercepterait ses clics — dont ceux de son
         CTA. On la rend traversante. */
      st.pointerEvents = vivant ? 'auto' : 'none';
      [this.gauche, this.centre, this.droite].forEach((el) => {
        if (el) el.style.pointerEvents = vivant ? 'auto' : 'none';
      });

      if (this.copy) {
        const c = Math.max(0, Math.min(1, (p - 0.2) / 0.16));
        this.copy.style.opacity = c.toFixed(3);
        this.copy.style.pointerEvents = c > 0.5 && vivant ? 'auto' : 'none';
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (REDUIT) return;
    /* Le verrou d'entrée est déjà posé : si le hero ne s'initialise pas, il
       faut le rendre, sinon la page reste bloquée en haut. */
    try {
      document.querySelectorAll('[data-hero-pin]').forEach((el) => new HeroPin(el));
    } catch (err) {
      verrou(false);
      throw err;
    }
    /* Mobile : les chapitres ne sont pas animés du tout. On ne les instancie
       donc pas — pas de ScrollTrigger, pas de styles en ligne posés dessus,
       la mise en page mobile de la feuille de style suffit à les rendre. */
    if (mobile()) return;
    document.querySelectorAll('[data-zoom-act]').forEach((el) => {
      if (el.classList.contains('zoom-act--galerie')) new ActeGalerie(el);
      else new ActeProfondeur(el);
    });
  });
})();
